import axios from 'axios';
import { API_BASE_URL } from './runtimeConfig';

const IDEMPOTENT_READ_METHODS = new Set(['get', 'head', 'options']);

export function isRetryableRead(error) {
  const config = error?.config;
  if (!config || config._retried) return false;
  const method = (config.method || 'get').toLowerCase();
  const isNetworkFailure = error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED';
  return isNetworkFailure && IDEMPOTENT_READ_METHODS.has(method);
}

// Storage helper — sessionStorage only (cleared on tab close for security)
export function getToken() {
  return sessionStorage.getItem('token') || null;
}

export function setToken(token) {
  sessionStorage.setItem('token', token);
}

export function removeToken() {
  sessionStorage.removeItem('token');
  localStorage.removeItem('token'); // Clean up legacy
}

export function getStoredUser() {
  try {
    const raw = sessionStorage.getItem('user');
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export function setStoredUser(user) {
  sessionStorage.setItem('user', JSON.stringify(user));
  localStorage.removeItem('user'); // Remove legacy persistent profile cache.
}

export function removeStoredUser() {
  sessionStorage.removeItem('user');
  localStorage.removeItem('user');
}

export const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
});

// Retry once only when replaying the request cannot duplicate a mutation.
api.interceptors.response.use(null, async (error) => {
  const config = error.config;
  if (isRetryableRead(error)) {
    config._retried = true;
    return api(config);
  }
  return Promise.reject(error);
});

// Attach token to every request
api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 — clear auth and redirect
let isRedirecting = false;
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && !isRedirecting) {
      isRedirecting = true;
      removeToken();
      removeStoredUser();
      // Use replace to avoid back-button loops
      window.location.replace('/login');
    }
    return Promise.reject(error);
  }
);

export default api;
